import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/calendar/calendar.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import '@chancenwerk/client/design/components/cr-data-view-table.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-dropdown-search-selection.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { WorkTrackingAPI } from '@chancenwerk/work-tracking/api/WorkTrackingAPI.js';
import {
  disableButton,
  editButton,
  finishedButton,
  historyButton,
  questionButton,
} from '@chancenwerk/work-tracking/client/actions.js';
import '@chancenwerk/work-tracking/client/chancen-component-work-tracking-add-single.js';
import { download } from '@adornis/file-utils/client/helper-methods.js';
import {
  createActionButtons,
  createKpfColumn,
  createLastNoteColumn,
  createPrefixColumns,
} from '@chancenwerk/work-tracking/client/data-view.js';
import { WorkTrackingStatusAspect } from '@chancenwerk/work-tracking/entitites/aspects/WorkTrackingStatusAspect.js';
import { WorkTrackingEntry } from '@chancenwerk/work-tracking/entitites/WorkTrackingEntry.js';
import { WorkTrackingTaetigkeit } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeit.js';
import { WorkTrackingTaetigkeitenState } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeitenState.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';

/**
 * @customElement board-page-work-tracking-op
 */
@customElement('board-page-work-tracking-op')
export class BoardPageWorkTrackingOP extends CrPage {
  constructor() {
    super();
    this.label = 'Tätigkeiten Verwaltung';
    this.description = 'Hier kannst du Tätigkeiten einsehen und verwalten';
    this.permission = ChancenPermission.board.EDIT_WORK_TRACKING;
  }

  @state() _viewForMonth = DateTime.now();
  @state() _createForMonth = DateTime.now();
  @state() _initPersonID = '';
  @state() _forDate = DateTime.now();
  @state() _selectedTab = 'Kurz-Übersicht';

  private _canSeeQuickView() {
    return (
      this._currentUser.value?.roles.some(r => r.name === Role.INTERNAL_KPF_OPERATIV || r.name === Role.ADMIN) ||
      this._currentUser.value?.hasPermission(ChancenPermission.board.EDIT_WORK_TRACKING_EVERYTHING, ANY_CONTEXT)
    );
  }

  override content() {
    if (baseConfig.get('DEPLOYMENT') === 'production') {
      return html``;
    }

    return html`
      <cr-grid columns="1fr max-content" space="md" padding="sm" ${css({ alignItems: 'start' })}>
        <cr-flex space="md">
          <cr-card space="md">
            <cr-tabs
              .selected=${['Kurz-Übersicht', 'Offene Tätigkeiten', 'Fertige Tätigkeiten'].indexOf(this._selectedTab)}
            >
              ${this._canSeeQuickView()
                ? html`<cr-tab @click=${() => (this._selectedTab = 'Kurz-Übersicht')}> Kurz-Übersicht </cr-tab>`
                : ''}
              <cr-tab @click=${() => (this._selectedTab = 'Offene Tätigkeiten')}> Offene Tätigkeiten </cr-tab>
              <cr-tab @click=${() => (this._selectedTab = 'Fertige Tätigkeiten')}> Fertige Tätigkeiten </cr-tab>
            </cr-tabs>

            ${this._selectedTab === 'Kurz-Übersicht' && this._canSeeQuickView()
              ? html`
                  <cr-text> Hier findest du eine Liste aller offenen Tätigkeiten! </cr-text>
                  ${this._quickTable()}
                `
              : ''}
            ${this._selectedTab === 'Offene Tätigkeiten'
              ? html`
                  <cr-text>
                    Die folgenden Tätigkeiten sind noch nicht vollständig geklärt und benötigen ihre Aufmerksamkeit!
                  </cr-text>
                  ${this._table([WorkTrackingTaetigkeitenState.OPEN, WorkTrackingTaetigkeitenState.QUESTION], true)}
                `
              : ''}
            ${this._selectedTab === 'Fertige Tätigkeiten'
              ? html`
                  <cr-flex horizontal space="md">
                    <cr-dropdown-selection
                      flex
                      placeholder="Monat"
                      .value=${this._viewForMonth.toFormat('MM')}
                      .selectables=${Array.from(new Array(12).keys())
                        .map(i => i + 1)
                        .map(i => i.toString().padStart(2, '0'))}
                      @value-picked=${(e: ValueEvent<Maybe<string>>) => {
                        if (!e.detail.value) {
                          return;
                        }

                        const month = parseInt(e.detail.value);
                        if (isNaN(month)) {
                          return;
                        }
                        this._viewForMonth = this._viewForMonth.set({ month });
                      }}
                    >
                    </cr-dropdown-selection>
                    <cr-dropdown-selection
                      flex
                      placeholder="Jahr"
                      .value=${this._viewForMonth.toFormat('yyyy')}
                      .selectables=${Array.from(new Array(10).keys())
                        .map(i => DateTime.now().year - i)
                        .map(i => i.toString())}
                      @value-picked=${(e: ValueEvent<Maybe<string>>) => {
                        if (!e.detail.value) {
                          return;
                        }

                        const year = parseInt(e.detail.value);
                        if (isNaN(year)) {
                          return;
                        }
                        this._viewForMonth = this._viewForMonth.set({ year });
                      }}
                    ></cr-dropdown-selection>
                    <cr-button
                      @click=${async () => {
                        await ChromiumDialog.guard(async () =>
                          download(
                            (await WorkTrackingAPI.datevExport(this._viewForMonth)()) ?? '',
                            `DATEV_${this._viewForMonth.toFormat('yyyy_MM')}.csv`,
                            'text/csv',
                          ),
                        );
                      }}
                    >
                      DATEV
                    </cr-button>
                  </cr-flex>
                  ${this._table([WorkTrackingTaetigkeitenState.FINISHED])}
                `
              : ''}
          </cr-card>
        </cr-flex>
        <cr-flex space="md">
          <cr-card flex ${css({ maxWidth: '300px' })} space="md" padding="md">
            <chancen-component-card-title>Person öffnen</chancen-component-card-title>
            ${this._initPerson()}
          </cr-card>
          <chancen-component-work-tracking-add-single></chancen-component-work-tracking-add-single>
          <cr-card flex ${css({ maxWidth: '300px' })} space="md" padding="md">
            <chancen-component-card-title>Erstellen</chancen-component-card-title>
            <cr-dropdown-selection
              flex
              placeholder="Monat"
              .value=${this._createForMonth.toFormat('MM')}
              .selectables=${Array.from(new Array(12).keys())
                .map(i => i + 1)
                .map(i => i.toString().padStart(2, '0'))}
              @value-picked=${(e: ValueEvent<Maybe<string>>) => {
                if (!e.detail.value) {
                  return;
                }

                const month = parseInt(e.detail.value);
                if (isNaN(month)) {
                  return;
                }
                this._createForMonth = this._createForMonth.set({ month });
              }}
            >
            </cr-dropdown-selection>
            <cr-dropdown-selection
              flex
              placeholder="Jahr"
              .value=${this._createForMonth.toFormat('yyyy')}
              .selectables=${Array.from(new Array(10).keys())
                .map(i => DateTime.now().year - i)
                .map(i => i.toString())}
              @value-picked=${(e: ValueEvent<Maybe<string>>) => {
                if (!e.detail.value) {
                  return;
                }

                const year = parseInt(e.detail.value);
                if (isNaN(year)) {
                  return;
                }
                this._createForMonth = this._createForMonth.set({ year });
              }}
            ></cr-dropdown-selection>
            <cr-button
              @click=${async () => {
                ChromiumDialog.guard(async () => {
                  await WorkTrackingAPI.initMonth(this._createForMonth)();
                });
              }}
              >Erstellen</cr-button
            >
          </cr-card>
        </cr-flex>
      </cr-grid>
    `;
  }

  private _initPerson() {
    return html`
      <x-entity-picker
        ${css({ maxWidth: '720px' })}
        placeholder="Person"
        clearable
        .options=${{
          class: ChancenwerkUser,
          selectionSet: { vorname: 1, nachname: 1 },
          operation: UserAPI.subscribeByFilter,
          filter: new ChancenwerkUserFilter({
            userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
          }),
        }}
        @value-picked=${async (e: ValueEvent<Maybe<string>>) => {
          if (!e.detail.value) {
            return;
          }

          this._initPersonID = e.detail.value;
        }}
      ></x-entity-picker>
      <cr-flex horizontal space="md">
        <cr-dropdown-selection
          flex
          placeholder="Monat"
          .value=${this._forDate.toFormat('MM')}
          .selectables=${Array.from(new Array(12).keys())
            .map(i => i + 1)
            .map(i => i.toString().padStart(2, '0'))}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => {
            if (!e.detail.value) {
              return;
            }

            const month = parseInt(e.detail.value);
            if (isNaN(month)) {
              return;
            }
            this._forDate = this._forDate.set({ month });
          }}
        >
        </cr-dropdown-selection>
        <cr-dropdown-selection
          flex
          placeholder="Jahr"
          .value=${this._forDate.toFormat('yyyy')}
          .selectables=${Array.from(new Array(10).keys())
            .map(i => i + 2000)
            .map(i => i.toString())}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => {
            if (!e.detail.value) {
              return;
            }

            const year = parseInt(e.detail.value);
            if (isNaN(year)) {
              return;
            }
            this._forDate = this._forDate.set({ year });
          }}
        ></cr-dropdown-selection>
      </cr-flex>
      <cr-button
        ?disabled=${!this._initPersonID}
        @click=${async () => {
          ChromiumDialog.guard(async () => {
            const res = await WorkTrackingAPI.createEntry(this._initPersonID, this._forDate)();
            goTo('/work-tracking/' + res);
          });
        }}
        >Person öffnen</cr-button
      >
    `;
  }

  private _quickTable() {
    return html`
      <x-data-view
        .options=${{
          class: WorkTrackingTaetigkeit,
          operation: WorkTrackingAPI.subscribeTaetigkeiten,
          view: new View({
            filter: new AdornisFilter({
              aspects: [
                new StringAspect({
                  fieldName: 'stateResponsible',
                  mode: 'containsOR',
                  value: `${WorkTrackingTaetigkeitenState.OPEN},${WorkTrackingTaetigkeitenState.QUESTION}`,
                }),
              ],
            }),
          }),
          isEntryClickable: false,
          onlyShowLastTranslation: true,
          selectionSet: { disabled: 1, generatedFromID: 1, isSingle: 1 },
          customColumns: [
            ...createPrefixColumns<WorkTrackingTaetigkeit>(this, () => false),
            {
              customID: 'user',
              columnName: 'Wer?',
              selectionSet: { user: { vorname: 1, nachname: 1 }, userID: 1, date: 1 },
              getFieldMapping: (t: WorkTrackingTaetigkeit) =>
                html`<cr-link
                  @click=${async () => {
                    const res = await WorkTrackingAPI.createEntry(t?.userID ?? '', t.date ?? DateTime.now())();
                    goTo('/work-tracking/' + res, { target: '_blank' });
                  }}
                >
                  <cr-text>${t.user?.vorname} ${t.user?.nachname}</cr-text>
                  <cr-tooltip><cr-text>Übersicht öffnen</cr-text></cr-tooltip>
                </cr-link>`,
            },
            {
              fieldName: 'isSingle',
              columnName: 'Einzeltätigkeit',
              getFieldMapping: (t: WorkTrackingTaetigkeit) => (t.isSingle ? 'Ja' : 'Nein'),
            },
            { fieldName: 'type', columnName: 'Art' },
            createKpfColumn(),
            createLastNoteColumn(),
            createActionButtons(
              this,
              t => finishedButton(this, t, this._currentUser.value?._id ?? ''),
              t => questionButton(this, t, this._currentUser.value?._id ?? ''),
              t => editButton(t, this._currentUser.value?._id ?? '', () => void 0),
              t => disableButton(this, t, this._currentUser.value?._id ?? '', () => void 0),
              t => historyButton(this, t),
              () => true,
            ),
          ],
        } as DataViewOptions<typeof WorkTrackingTaetigkeit>}
      >
      </x-data-view>
    `;
  }

  private _table(status?: WorkTrackingTaetigkeitenState[], noMonthFilter = false) {
    return html`
      <x-data-view
        .options=${{
          class: WorkTrackingEntry,
          operation: WorkTrackingAPI.subscribeEntries,
          view: new View({
            filter: new AdornisFilter({
              aspects: [
                ...(noMonthFilter
                  ? []
                  : [new StringAspect({ customID: 'month', value: this._viewForMonth.toFormat('MM') })]),
                ...(noMonthFilter
                  ? []
                  : [new StringAspect({ customID: 'year', value: this._viewForMonth.toFormat('yyyy') })]),
                ...(status ? [new WorkTrackingStatusAspect({ customID: 'status', values: status })] : []),
              ],
            }),
          }),
          rowHref(entity) {
            return '/work-tracking/' + entity._id;
          },
          isEntryClickable: false,
          onlyShowLastTranslation: true,
          customColumns: [
            {
              fieldName: 'date',
              columnName: 'Monat',
              columnWidth: 'max-content',
              getFieldMapping: (t: WorkTrackingEntry) => t.date?.toFormat('MM.'),
            },
            {
              fieldName: 'user',
              columnName: 'Wer?',
              columnWidth: '250px',
              selectionSet: { user: { vorname: 1, nachname: 1, email: 1 } },
            },
            {
              fieldName: 'stateResponsible',
              columnName: 'Verwaltung',
              columnWidth: '150px',
              renderItem: (t: WorkTrackingEntry) => {
                return this._stateTemplate(t.stateResponsible ?? WorkTrackingTaetigkeitenState.OPEN);
              },
            },
            {
              fieldName: 'stateContractor',
              columnName: 'Person',
              columnWidth: '150px',
              renderItem: (t: WorkTrackingEntry) => {
                return this._stateTemplate(t.stateContractor ?? WorkTrackingTaetigkeitenState.OPEN);
              },
            },
            {
              fieldName: 'moneySum',
              columnName: '€',
              columnWidth: '100px',
              getFieldMapping: (t: WorkTrackingEntry) => {
                return html`<cr-text ${css({ fontFamily: 'monospace' })}>${(t.moneySum ?? 0).toFixed(2)}€</cr-text>`;
              },
            },
            { fieldName: 'entryCount', columnName: 'Einträge', columnWidth: 'max-content' },
            {
              fieldName: 'lastChange',
              columnName: 'Letzte Änderung',
              getFieldMapping: (t: WorkTrackingEntry) => t.lastChange?.toFormat('dd.MM.yyyy HH:mm'),
            },
          ],
        } as DataViewOptions<typeof WorkTrackingEntry>}
      >
      </x-data-view>
    `;
  }

  private _stateTemplate(s: WorkTrackingTaetigkeitenState) {
    return html`
      <cr-flex horizontal space="sm" crossaxis-center ${css({ color: this._stateColor(s) })}>
        <cr-icon> ${this._stateIcon(s)} </cr-icon>
        <cr-text> ${s} </cr-text>
      </cr-flex>
    `;
  }

  private _stateIcon(s: WorkTrackingTaetigkeitenState) {
    switch (s) {
      case WorkTrackingTaetigkeitenState.OPEN:
        return 'question_mark';
      case WorkTrackingTaetigkeitenState.FINISHED:
        return 'check';
      case WorkTrackingTaetigkeitenState.QUESTION:
        return 'warning';
    }
  }

  private _stateColor(s: WorkTrackingTaetigkeitenState) {
    switch (s) {
      case WorkTrackingTaetigkeitenState.OPEN:
        return this.colors.neutral;
      case WorkTrackingTaetigkeitenState.FINISHED:
        return this.colors.tone.success;
      case WorkTrackingTaetigkeitenState.QUESTION:
        return this.colors.tone.warning;
    }
  }
}
